"use client"

import { useState, useEffect } from "react"
import useAuth from "../../hooks/useAuth"
import { useNavigate, Link } from "react-router-dom"
import axios from "axios"
import Loading from "../../components/Loading"
import { XIcon } from "@heroicons/react/outline"
import EditTestModal from "../../components/EditTestModal"
import DeleteTestModal from "../../components/DeleteTestModal"
import ShareTestModal from "../../components/ShareTestModal"
import { FaPaperPlane, FaPen, FaShare, FaLock } from "react-icons/fa"

function extractFunction(text) {
  const parts = text.split("-")
  if (parts.length > 1) {
    const functionPart = parts[1].split("/")[0]
    const secondFunctionPart = parts[1].split("/")[1].split("-")[0]
    return `${functionPart}/${secondFunctionPart}`
  }
  return text
}

function ResultText({ result }) {
  const [text, setText] = useState("")
  const [imageUrl, setImageUrl] = useState("")
  const [textReady, setTextReady] = useState(false)

  useEffect(() => {
    const fetchData = async () => {
      const token =
        "6b1875cbfb2e50bbe3805dbb0385a8b14e707bdbf2325674ddaad125239e7d3401f8aa2cca4094a3ffa38f6f790d510ada111a5f03626ebe6661ddbf95456582d5fa9795ee4c2958fb72a8cf3187897b219cd2551eddf83e2232de063f0a95c4ebad38bd5245e3a18e986a939db189a728793103eba8c2fe75128c921d7085d3"
      const cmsIdentifier = extractFunction(result)
      try {
        const response = await axios.get(
          `https://cms.typescience.ai/api/personality-types?filters[unique_name][$eq]=${cmsIdentifier}&populate=*`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        )
        setTextReady(true)
        setImageUrl(response?.data?.data[0]?.attributes?.image?.data?.attributes?.url)
        setText(response?.data?.data[0]?.attributes?.name + " " + response?.data?.data[0]?.attributes?.description)
      } catch (error) {
        console.error("Error fetching personality types:", error)
        return ""
      }
    }
    if (result) {
      fetchData()
    }
  }, [result]) // Re-run this effect if `queryId` changes

  return (
    <div className="flex items-center gap-1 p-0.5">
      {imageUrl && (
        <div className="flex-shrink-0 rounded-full overflow-hidden">
          <object className="w-7 h-7 object-cover" data={"https://cms.typescience.ai" + imageUrl} type="image/jpeg">
            <img
              className="w-7 h-7 object-cover"
              src={"https://cms.typescience.ai" + imageUrl}
              alt="Result icon"
              loading="lazy"
            />
          </object>
        </div>
      )}
      <span className="text-xs font-medium text-gray-700 line-clamp-1">{textReady ? text : "Loading..."}</span>
    </div>
  )
}

const DashboardHome = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [testData, setTestData] = useState({
    tests: [],
    shared_tests: [],
    merged_tests: [],
  })
  const [selectedRows, setSelectedRows] = useState([])
  const [selectedTest, setSelectedTest] = useState({})
  const [isEditTestModalOpen, setEditTestModalOpen] = useState(false)
  const [isDeleteTestModalOpen, setDeleteTestModalOpen] = useState(false)
  const [isShareTestModalOpen, setShareTestModalOpen] = useState(false)
  const [isSendTestModalOpen, setSendTestModalOpen] = useState(false)
  const [copiedTest, setCopiedTest] = useState()
  const [showTooltip, setShowTooltip] = useState(false)

  const { isUserLoggedIn, logout, user } = useAuth()

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(`${process.env.REACT_APP_PUBLIC_URL}/share?id=${text}`)
      setShowTooltip(true)
      setTimeout(() => setShowTooltip(false), 2000)
      setCopiedTest(text)
    } catch (err) {
      setCopiedTest("")
    }
  }

  const handleModalClose = () => {
    setEditTestModalOpen(false)
    setDeleteTestModalOpen(false)
    setShareTestModalOpen(false)
    setSendTestModalOpen(false)
    setSelectedTest({})
  }

  const handleLockClick = async (test) => {
    setIsLoading(true)
    console.log(test)
    try {
      const response = await axios.post(
        `https://xbvh-ofc9-nsmy.n7c.xano.io/api:zhMAliV-:v1/session?share_id=${test.share_id}`,
      ) // Replace with your actual API endpoint
      console.log(response?.data?.url)
      window.open(response?.data?.url)
      setIsLoading(false)
    } catch (error) {
      console.error("Error fetching test data:", error)
      setIsLoading(false)
    }
  }

  const handleEditTest = (test) => {
    console.log(test)
    setSelectedTest(test)
    setEditTestModalOpen(true)
  }

  const handleUpdateTest = async (data, updatedName) => {
    setIsLoading(true)
    await axios
      .put(`https://xbvh-ofc9-nsmy.n7c.xano.io/api:PW99k_MF/test/${data.id}`, {
        name: updatedName,
      })
      .then((response) => {
        console.log("Response:", response.data)
        fetchData()
        setIsLoading(false)
        setEditTestModalOpen(false)
        setSelectedTest({})
      })
      .catch((error) => {
        console.error("Error:", error)
        setIsLoading(false)
        setEditTestModalOpen(false)
      })
  }

  const handleCheckboxChange = (testId, isChecked) => {
    if (isChecked) {
      setSelectedRows([...selectedRows, testId])
    } else {
      setSelectedRows(selectedRows.filter((id) => id !== testId))
    }
  }

  const isButtonActive = selectedRows.length >= 2
  const navigate = useNavigate()

  const resolveResultName = async (testName) => {}

  const handleMergeTests = async () => {
    setIsLoading(true)
    await axios
      .post(`https://xbvh-ofc9-nsmy.n7c.xano.io/api:PW99k_MF/merge?ids=[${selectedRows}]`, {})
      .then((response) => {
        navigate(`/dashboard/results?id=${response?.data?.id}`)
        console.log("Response:", response.data)
        setIsLoading(false)
      })
      .catch((error) => {
        console.error("Error:", error)
        setIsLoading(false)
      })
  }

  const handleDeleteTestClick = (test) => {
    console.log(test)
    setSelectedTest(test)
    setDeleteTestModalOpen(true)
  }

  const handleDeleteTest = async (data) => {
    setIsLoading(true)
    console.log(data)
    await axios
      .delete(`https://xbvh-ofc9-nsmy.n7c.xano.io/api:PW99k_MF/test/${data}`, {})
      .then((response) => {
        fetchData()
        setIsLoading(false)
        setDeleteTestModalOpen(false)
      })
      .catch((error) => {
        console.error("Error:", error)
        setIsLoading(false)
        setDeleteTestModalOpen(false)
      })
  }

  const handleShareTest = (test) => {
    console.log(test)
    setSelectedTest(test)
    setShareTestModalOpen(true)
  }

  const handleSendTest = (test) => {
    console.log(test)
    setSelectedTest(test)
    setSendTestModalOpen(true)
  }

  const fetchData = async () => {
    setIsLoading(true)
    try {
      const response = await axios.get("https://xbvh-ofc9-nsmy.n7c.xano.io/api:PW99k_MF:v1/test") // Replace with your actual API endpoint
      setTestData(response.data)
      setIsLoading(false)
    } catch (error) {
      console.error("Error fetching test data:", error)
      setIsLoading(false)
    }
  }

  const redirectToPayment = async (shareId) => {
    try {
      const response = await axios.post(
        `https://xbvh-ofc9-nsmy.n7c.xano.io/api:zhMAliV-:v1/session?share_id=${shareId}`,
      )
      if (response?.data?.url) {
        window.open(response?.data?.url)
      }
      setIsLoading(false)
    } catch (error) {
      console.error("Error fetching test data:", error)
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
    setShowTooltip(false)
    const params = new URLSearchParams(location.search)
    const unlock = params.get("unlock")
    const shareId = params.get("share_id")
    if (unlock && shareId) {
      redirectToPayment(shareId)
    }
  }, [isUserLoggedIn])

  return (
    <div className="bg-[#F7F5FF] min-h-screen md:pl-28">
      {/* Main content area - adjusted to account for sidebar */}
      <div className="py-5 px-3 sm:py-7 sm:px-5">
        <div className="max-w-full mx-auto flex flex-col gap-4">
          {/* Header with Take Test button */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between bg-white rounded-lg shadow-sm px-5 py-4 sm:px-6 sm:py-5 mb-5 gap-3">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Dashboard</h1>
            <a
              href="/test"
              className="w-full sm:w-auto rounded-full bg-[#7035de] hover:bg-[#5a28b5] text-white py-2 px-4 sm:px-6 text-sm font-semibold transition-colors duration-200 text-center"
            >
              Take Test
            </a>
          </div>

          <div className="flex flex-col lg:flex-row gap-4">
            {/* Main Dashboard Cards */}
            <div className="flex-1 flex flex-col gap-5">
              {/* Info Card + Merge Button */}
              <div className="bg-white rounded-lg shadow-sm p-5 sm:p-6">
                <p className="text-xs sm:text-sm text-gray-700 mb-4 leading-relaxed">
                  To get the most accurate test result for yourself, have people who know you well take the test on your
                  behalf, share their results with you, then select the results here and select{" "}
                  <span className="font-semibold text-[#7035de]">"Merge"</span> to get an averaged result of their
                  opinions of you.
                </p>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 gap-3">
                  <h2 className="text-base sm:text-lg font-bold text-gray-800">My Tests</h2>
                  <button
                    className={`w-full sm:w-auto inline-flex items-center justify-center rounded-full bg-[#7035de] hover:bg-[#5a28b5] px-4 sm:px-6 py-2 text-xs sm:text-sm font-semibold text-white focus:outline-none transition-colors duration-200 ${!isButtonActive ? "opacity-50 cursor-not-allowed" : ""}`}
                    disabled={!isButtonActive}
                    onClick={handleMergeTests}
                  > Merge
                  </button>
                </div>

                <div className="overflow-x-auto w-full max-w-full">
                  <table className="min-w-full table-fixed text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="py-3 pl-3 pr-2 text-left font-semibold text-gray-700 tracking-wider"> </th>
                        <th className="py-3 pl-2 pr-2 text-left font-semibold text-gray-700 tracking-wider">Name</th>
                        <th className="py-3 px-2 text-left font-semibold text-gray-700 tracking-wider">Edit</th>
                        <th className="py-3 px-2 text-left font-semibold text-gray-700 tracking-wider">Result</th>
                        <th className="py-3 px-2 text-left font-semibold text-gray-700 tracking-wider">Send</th>
                        <th className="py-3 px-2 text-left font-semibold text-gray-700 tracking-wider">Remove</th>
                        <th className="py-3 px-2 text-left font-semibold text-gray-700 tracking-wider">Share</th>
                        <th className="py-3 px-2 text-left font-semibold text-gray-700 tracking-wider">Lock</th>
                      </tr>
                    </thead>
                    <tbody>
                      {testData.tests.length > 0 ? (
                        testData.tests.map((test, personIdx) => (
                          <tr
                            key={test.email}
                            className={personIdx % 2 === 0 ? "bg-white hover:bg-gray-50" : "bg-gray-50 hover:bg-gray-50"}
                          >
                            <td className="whitespace-nowrap py-2 pl-3 pr-2 font-medium text-gray-900">
                              <input
                                type="checkbox"
                                className="h-4 w-4 text-[#7035de] focus:ring-[#7035de] rounded-md"
                                onChange={(e) => handleCheckboxChange(test.id, e.target.checked)}
                                checked={selectedRows.includes(test.id)}
                              />
                            </td>
                            <td className="whitespace-nowrap py-2 pl-2 pr-2 font-medium text-[#7035de] hover:text-[#5a28b5] hover:underline truncate max-w-[100px] sm:max-w-[150px] transition-colors duration-200">
                              <Link to={`/dashboard/results?id=${test.id}`}>{test.name}</Link>
                            </td>
                            <td className="whitespace-nowrap px-2 py-2">
                              <button
                                onClick={() => handleEditTest(test)}
                                className="w-full sm:w-auto rounded-full bg-[#7035de] hover:bg-[#5a28b5] flex items-center justify-center gap-1 px-2 py-1.5 text-xs text-white font-medium transition-colors duration-200"
                              >
                                <FaPen className="h-3 w-3" /> <span className="hidden xs:inline">Rename</span>
                              </button>
                            </td>
                            <td className="whitespace-nowrap px-2 py-2 w-lg">
                              <ResultText result={test.result} />
                            </td>
                            <td className="whitespace-nowrap px-2 py-2">
                              <button
                                onClick={() => handleSendTest(test)}
                                className="w-full sm:w-auto rounded-full bg-[#7035de] hover:bg-[#5a28b5] flex items-center justify-center gap-1 px-2 py-1.5 text-xs text-white font-medium transition-colors duration-200"
                              >
                                <FaPaperPlane className="h-3 w-3" /> <span className="hidden xs:inline">Send</span>
                              </button>
                            </td>
                            <td className="whitespace-nowrap px-2 py-2">
                              <button
                                onClick={() => handleDeleteTestClick(test)}
                                className="w-full sm:w-auto rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center gap-1 px-2 py-1.5 text-xs text-white font-medium transition-colors duration-200"
                              >
                                <XIcon className="h-3 w-3" /> <span className="hidden xs:inline">Remove</span>
                              </button>
                            </td>
                            <td className="whitespace-nowrap px-2 py-2">
                              <button
                                onClick={() => handleShareTest(test)}
                                className="w-full sm:w-auto rounded-full bg-[#7035de] hover:bg-[#5a28b5] flex items-center justify-center gap-1 px-2 py-1.5 text-xs text-white font-medium transition-colors duration-200"
                              >
                                <FaShare className="h-3 w-3" /> <span className="hidden xs:inline">Share</span>
                              </button>
                            </td>
                            <td className="whitespace-nowrap px-2 py-2">
                              {test.is_paid ? (
                                ""
                              ) : (
                                <div
                                  onClick={() => handleLockClick(test)}
                                  className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-gray-600 cursor-pointer hover:bg-gray-200 transition-colors duration-200"
                                >
                                  <FaLock className="h-3 w-3" />
                                </div>
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="8" className="text-center py-4 text-xs text-gray-500 italic">
                            No data available.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Shared Tests Section */}
              <div className="bg-white rounded-lg shadow-sm p-5 sm:p-6">
                <h2 className="text-base sm:text-lg font-bold text-gray-800 mb-3">Shared Tests</h2>
                <div className="overflow-x-auto w-full max-w-full">
                  <table className="min-w-full table-fixed text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="py-3 pl-3 pr-2 text-left font-semibold text-gray-700 tracking-wider"> </th>
                        <th className="py-3 pl-2 pr-2 text-left font-semibold text-gray-700 tracking-wider">Name</th>
                        <th className="py-3 px-2 text-left font-semibold text-gray-700 tracking-wider">Edit</th>
                        <th className="py-3 px-2 text-left font-semibold text-gray-700 tracking-wider">Shared From</th>
                        <th className="py-3 px-2 text-left font-semibold text-gray-700 tracking-wider">Result</th>
                      </tr>
                    </thead>
                    <tbody>
                      {testData.shared_tests.length > 0 ? (
                        testData.shared_tests.map((test, personIdx) => (
                          <tr
                            key={test.email}
                            className={personIdx % 2 === 0 ? "bg-white hover:bg-gray-50" : "bg-gray-50 hover:bg-gray-50"}
                          >
                            <td className="whitespace-nowrap py-2 pl-3 pr-2 font-medium text-gray-900">
                              <input
                                type="checkbox"
                                className="h-4 w-4 text-[#7035de] focus:ring-[#7035de] rounded-md"
                                onChange={(e) => handleCheckboxChange(test.id, e.target.checked)}
                                checked={selectedRows.includes(test.id)}
                              />
                            </td>
                            <td className="whitespace-nowrap py-2 pl-2 pr-2 font-medium text-[#7035de] hover:text-[#5a28b5] hover:underline truncate max-w-[100px] sm:max-w-[150px] transition-colors duration-200">
                              <Link to={`/dashboard/results?id=${test.id}`}>{test.name}</Link>
                            </td>
                            <td className="whitespace-nowrap px-2 py-2">
                              <button
                                onClick={() => handleEditTest(test)}
                                className="w-full sm:w-auto rounded-full bg-[#7035de] hover:bg-[#5a28b5] flex items-center justify-center gap-1 px-2 py-1.5 text-xs text-white font-medium transition-colors duration-200"
                              >
                                <FaPen className="h-3 w-3" /> <span className="hidden xs:inline">Rename</span>
                              </button>
                            </td>
                            <td className="whitespace-nowrap px-2 py-2 text-gray-700 font-medium">
                              {test?.taker?.email}
                            </td>
                            <td className="whitespace-nowrap px-2 py-2">
                              <ResultText result={test.result} />
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="text-center py-4 text-xs text-gray-500 italic">
                            No data available.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Merged Tests Section */}
              <div className="bg-white rounded-lg shadow-sm p-5 sm:p-6">
                <h2 className="text-base sm:text-lg font-bold text-gray-800 mb-3">Merged Tests</h2>
                <div className="overflow-x-auto w-full max-w-full">
                  <table className="min-w-full table-fixed text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="py-3 pl-3 pr-2 text-left font-semibold text-gray-700 tracking-wider">Name</th>
                        <th className="py-3 px-2 text-left font-semibold text-gray-700 tracking-wider">Edit</th>
                        <th className="py-3 px-2 text-left font-semibold text-gray-700 tracking-wider">Result</th>
                        <th className="py-3 px-2 text-left font-semibold text-gray-700 tracking-wider">Send</th>
                        <th className="py-3 px-2 text-left font-semibold text-gray-700 tracking-wider">Share</th>
                        <th className="py-3 px-2 text-left font-semibold text-gray-700 tracking-wider">Remove</th>
                        <th className="py-3 px-2 text-left font-semibold text-gray-700 tracking-wider">Lock</th>
                      </tr>
                    </thead>
                    <tbody>
                      {testData.merged_tests.length > 0 ? (
                        testData.merged_tests.map((test, personIdx) => (
                          <tr
                            key={test.email}
                            className={personIdx % 2 === 0 ? "bg-white hover:bg-gray-50" : "bg-gray-50 hover:bg-gray-50"}
                          >
                            <td className="whitespace-nowrap py-2 pl-3 pr-2 font-medium text-[#7035de] hover:text-[#5a28b5] hover:underline truncate max-w-[100px] sm:max-w-[150px] transition-colors duration-200">
                              <Link to={`/dashboard/results?id=${test.id}`}>{test.name}</Link>
                            </td>
                            <td className="whitespace-nowrap px-2 py-2">
                              <button
                                onClick={() => handleEditTest(test)}
                                className="w-full sm:w-auto rounded-full bg-[#7035de] hover:bg-[#5a28b5] flex items-center justify-center gap-1 px-2 py-1.5 text-xs text-white font-medium transition-colors duration-200"
                              >
                                <FaPen className="h-3 w-3" /> <span className="hidden xs:inline">Rename</span>
                              </button>
                            </td>
                            <td className="whitespace-nowrap px-2 py-2">
                              <ResultText result={test.result} />
                            </td>
                            <td className="whitespace-nowrap px-2 py-2">
                              <button
                                onClick={() => handleSendTest(test)}
                                className="w-full sm:w-auto rounded-full bg-[#7035de] hover:bg-[#5a28b5] flex items-center justify-center gap-1 px-2 py-1.5 text-xs text-white font-medium transition-colors duration-200"
                              >
                                <FaPaperPlane className="h-3 w-3" /> <span className="hidden xs:inline">Send</span>
                              </button>
                            </td>
                            <td className="whitespace-nowrap px-2 py-2">
                              <button
                                onClick={() => handleShareTest(test)}
                                className="w-full sm:w-auto rounded-full bg-[#7035de] hover:bg-[#5a28b5] flex items-center justify-center gap-1 px-2 py-1.5 text-xs text-white font-medium transition-colors duration-200"
                              >
                                <FaShare className="h-3 w-3" /> <span className="hidden xs:inline">Share</span>
                              </button>
                            </td>
                            <td className="whitespace-nowrap px-2 py-2">
                              <button
                                onClick={() => handleDeleteTest(test)}
                                className="w-full sm:w-auto rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center gap-1 px-2 py-1.5 text-xs text-white font-medium transition-colors duration-200"
                              >
                                <XIcon className="h-3 w-3" /> <span className="hidden xs:inline">Remove</span>
                              </button>
                            </td>
                            <td className="whitespace-nowrap px-2 py-2">
                              {test.is_paid ? (
                                ""
                              ) : (
                                <div
                                  onClick={() => handleLockClick(test)}
                                  className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-gray-600 cursor-pointer hover:bg-gray-200 transition-colors duration-200"
                                >
                                  <FaLock className="h-3 w-3" />
                                </div>
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7" className="text-center py-4 text-xs text-gray-500 italic">
                            No data available.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Promo Card */}
            <div className="w-full lg:w-80 flex-shrink-0 order-first lg:order-none lg:ml-4">
              <div className="bg-white rounded-lg shadow-sm p-5">
                <div className="flex justify-center mb-3">
                  <img src="/promo-code.png" width={120} alt="Promo code" />
                </div>

                <h3 className="text-lg font-bold text-gray-800 mb-2 text-center">Full Results Promo Code</h3>

                <p className="my-2 text-xs text-gray-700 leading-relaxed">
                  Want to buy a test for later, or send one as a gift to friends and family? Promo codes are single-use
                  codes that never expire and can be redeemed to unlock a premium result. You can purchase a single
                  code, or get one <span className="font-bold text-[#7035de]">FREE</span> when you buy two!
                </p>

                <div className="grid grid-cols-2 gap-3 mt-3">
                  <button
                    onClick={() =>
                      window.open(
                        "https://buy.stripe.com/14kdTL0o4aTo4x2dQQ?client_reference_id=promo_single",
                        "_blank",
                      )
                    }
                    className="bg-[#7035de] text-white hover:bg-[#5a28b5] rounded-full py-2 px-3 font-medium transition-colors duration-200 text-xs"
                  >
                    Buy One
                  </button>
                  <button
                    onClick={() =>
                      window.open("https://buy.stripe.com/dR65nf6Ms7Hc1kQcMN?client_reference_id=promo_three", "_black")
                    }
                    className="bg-[#7035de] text-white hover:bg-[#5a28b5] rounded-full py-2 px-3 font-medium transition-colors duration-200 text-xs"
                  >
                    Buy 3-pack
                  </button>
                </div>

                <div className="mt-3 p-2 bg-gray-50 rounded-md">
                  <p className="text-xs text-gray-600 text-center">
                    Use promo codes to unlock premium features and detailed personality insights!
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded-lg">
            <Loading />
          </div>
        </div>
      )}

      {/* Only render modals when they are open */}
      {isEditTestModalOpen && (
        <EditTestModal
          open={isEditTestModalOpen}
          setOpen={handleModalClose}
          updateTest={handleUpdateTest}
          test={selectedTest}
        />
      )}

      {isDeleteTestModalOpen && (
        <DeleteTestModal
          open={isDeleteTestModalOpen}
          setOpen={handleModalClose}
          onDelete={handleDeleteTest}
          test={selectedTest}
        />
      )}

      {isShareTestModalOpen && (
        <ShareTestModal
          open={isShareTestModalOpen}
          setOpen={handleModalClose}
          test={selectedTest}
          withSend={false}
          withPublicLink={true}

        />
      )}

      {isSendTestModalOpen && (
        <ShareTestModal
          open={isSendTestModalOpen}
          setOpen={handleModalClose}
          test={selectedTest}
          withSend={true}
          withPublicLink={false}

        />
      )}
    </div>
  )
}

export default DashboardHome
